import { useState, useEffect } from "react";
import { Target, Eye, Heart, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import AnimatedSection from "@/components/AnimatedSection";
import FloatingOrbs from "@/components/FloatingOrbs";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const values = [
  { icon: Target, title: "Precision", desc: "Every solution is engineered to solve your specific business challenges." },
  { icon: Eye, title: "Transparency", desc: "No black boxes. We build AI you understand and trust." },
  { icon: Heart, title: "Responsibility", desc: "Ethical AI practices embedded in everything we create." },
  { icon: Sparkles, title: "Innovation", desc: "Cutting-edge technology made accessible for growing businesses." },
];

export default function AboutPage() {
  const [pageData, setPageData] = useState(null);
  useEffect(() => { axios.get(`${API}/pages/about`).then(r => setPageData(r.data)).catch(() => {}); }, []);

  const getSection = (key, fallback = "") => {
    if (!pageData) return fallback;
    const s = pageData.sections?.find(s => s.key === key);
    return s?.value || fallback;
  };

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 px-6 md:px-12 relative overflow-hidden" data-testid="about-hero">
        <FloatingOrbs />
        <div className="absolute top-20 right-0 w-72 h-72 bg-amber/5 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <AnimatedSection>
                <span className="text-xs font-semibold tracking-wider uppercase text-amber mb-4 block">About brgt.ai</span>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                  {getSection("headline", "We Saw the AI Gap. We Built the Bridge.")}
                </h1>
                <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-xl">
                  {getSection("subheadline", "brgt.ai was founded on one belief: every growing business deserves access to AI that\u2019s powerful, responsible, and built to scale with them.")}
                </p>
              </AnimatedSection>
            </div>
            <div className="lg:col-span-5 hidden lg:block">
              <AnimatedSection delay={0.2}>
                <img
                  src="https://images.unsplash.com/photo-1622675363311-3e1904dc1885?crop=entropy&cs=srgb&fm=jpg&w=500&q=80"
                  alt="Team"
                  className="rounded-2xl shadow-xl"
                />
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <AnimatedSection>
        <section className="py-20 md:py-28 px-6 md:px-12 bg-white" data-testid="about-story">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
              <div className="lg:col-span-5">
                <span className="text-xs font-semibold tracking-wider uppercase text-amber mb-3 block">Our Story</span>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
                  Bridging the AI Divide
                </h2>
              </div>
              <div className="lg:col-span-7">
                <p className="text-slate-600 leading-relaxed mb-6">
                  {getSection("story", "The AI revolution is transforming industries \u2014 but most of the benefits are locked inside large enterprises with deep pockets and dedicated teams. SMEs are left with off-the-shelf tools that barely scratch the surface.")}
                </p>
                <p className="text-slate-600 leading-relaxed mb-6">
                  We started brgt.ai to change that. Our team combines deep technical expertise with a genuine understanding of the challenges growing businesses face every day.
                </p>
                <div className="border-l-4 border-amber pl-6 py-2">
                  <p className="text-navy font-medium italic text-lg">
                    "{getSection("mission", "To bridge the AI gap for small and mid-scale entities by delivering AI solutions that are accessible, scalable, and built to generate real value \u2014 responsibly.")}"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Values */}
      <AnimatedSection>
        <section className="py-20 md:py-28 px-6 md:px-12 bg-surface" data-testid="about-values">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-xs font-semibold tracking-wider uppercase text-amber mb-3 block">Our Values</span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white">What Drives Us</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((v, i) => (
                <AnimatedSection key={v.title} delay={i * 0.1}>
                  <motion.div
                    whileHover={{ y: -6, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="bg-white rounded-2xl border border-slate-100 p-8 transition-shadow duration-300 hover:shadow-xl hover:shadow-amber/5 group cursor-default"
                    data-testid={`value-card-${i}`}
                  >
                    <div className="w-12 h-12 bg-amber/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-amber/20 transition-colors">
                      <v.icon size={24} className="text-amber" />
                    </div>
                    <h3 className="font-heading font-semibold text-navy text-lg mb-2">{v.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{v.desc}</p>
                  </motion.div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>
    </div>
  );
}
